import { useEffect, useRef } from "react";
import { styled } from "styled-components";

import { LeftArrow, UIButton } from "common/components";
import { mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { useSession } from "context/session/useSession";
import { fetchCollectionForViewing } from "domains/collection/actions";
import { getCollectionForViewing, getFeaturedCollections } from "domains/collection/selectors";
import { enterAIGeneratedMap, removeAllTopicsFromMap } from "domains/createMapDetail/actions";
import { getIsAIGeneratedModeOn } from "domains/createMapDetail/selectors";
import { useTrackDimensions } from "domains/dimension/useTrackDimensions";
import { getSiteLanguage } from "domains/language/selectors";
import { AdvancedOptionsDropdown } from "domains/topicDetail/components/advancedOptions/AdvancedOptionsDropdown";
import { AIPrompt } from "domains/topicDetail/components/AIPrompt/AIPrompt";
import { useTopicNavigation } from "domains/topicDetail/utils";
import { TransformationMapHistoryButton } from "domains/transformationMap/components/TransformationMapHistoryButton";
import { getTmEditMode } from "domains/transformationMap/selectors";
import { getCanAccessDiscoverTMAdvancedMode, getCanAccessGenerativeMaps } from "entities/permissions/selectors";

const Container = styled.div<{ align?: string }>`
  display: flex;
  gap: 0.5rem;
  inset: 0 0 auto 0;
  justify-content: ${({ align }) => (align === "right" ? "flex-end" : "space-between")};
  pointer-events: none;
  position: absolute;

  ${mediaStyles(({ spacing }) => `padding: ${spacing}rem;`)}

  > * {
    pointer-events: initial;
  }
`;

const StyledActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const StyledLeftArrow = styled(LeftArrow)`
  width: 14px;
  height: 14px;
  margin-right: 0.5rem;
`;

const Cell = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const BackButton = styled(UIButton)`
  background-color: ${({ theme }) => theme.colors.buttonNeutralBackground}b0;
  height: 2.5rem;
`;

const BackButtonLabel = styled.span`
  max-width: 18rem;
  overflow: clip;
  text-overflow: ellipsis;
`;

export const TopicDetailTopBar = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const translate = useTranslate();
  const accessToAdvancedMode = useSelector(getCanAccessDiscoverTMAdvancedMode);
  const accessToGenerativeMaps = useSelector(getCanAccessGenerativeMaps);
  const collection = useSelector(getCollectionForViewing);
  const isAIGeneratedModeOn = useSelector(getIsAIGeneratedModeOn);
  const featured = useSelector(getFeaturedCollections);
  const siteLanguage = useSelector(getSiteLanguage);
  const isEditing = useSelector(getTmEditMode);
  const { topicBack } = useSession();
  const topicNavigation = useTopicNavigation();
  const track = useTrack();
  const { trackRemoveAllDimensions } = useTrackDimensions();

  useEffect(() => {
    if (topicBack?.collection?.id !== undefined) dispatch(fetchCollectionForViewing(topicBack.collection.id));
  }, [dispatch, topicBack, siteLanguage]);

  const path = topicBack?.path ?? topicNavigation.path;

  const label =
    topicBack === undefined
      ? topicNavigation.label
      : topicBack.term !== undefined
        ? translate(topicBack.term)
        : collection === undefined
          ? featured.find((collection) => collection.id === topicBack.collection.id)?.name
          : collection.name;

  const labelRef = useRef<string | undefined>(label);

  useEffect(() => {
    if (label !== undefined) labelRef.current = label;
  }, [label]);

  const onClearHandle = () => {
    dispatch(removeAllTopicsFromMap());
    trackRemoveAllDimensions();
  };

  const enterAIGeneratedHandle = () => {
    track({ name: "Open Map Assistant", page: "Create" });
    dispatch(enterAIGeneratedMap());
  };

  return (
    <Container align={isEditing ? "right" : undefined}>
      {!isEditing ? (
        <>
          <BackButton onClick={() => go(path)} variant="neutral">
            <StyledLeftArrow />
            <BackButtonLabel>{labelRef.current ?? ""}</BackButtonLabel>
          </BackButton>
          <Cell>
            <TransformationMapHistoryButton />
            {accessToAdvancedMode && <AdvancedOptionsDropdown />}
          </Cell>
        </>
      ) : (
        <StyledActionButtons>
          <UIButton variant="neutral" onClick={onClearHandle}>
            {translate("app.create.create_map.clear_map")}
          </UIButton>
          {accessToGenerativeMaps && (
            <UIButton data-test-id="enter-map-assistant-button" onClick={enterAIGeneratedHandle} variant="filled">
              {translate("app.ai_map.assisted_map")}
            </UIButton>
          )}
        </StyledActionButtons>
      )}
      {isAIGeneratedModeOn && <AIPrompt />}
    </Container>
  );
};
